import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  MessageCircle,
  Calendar,
} from "lucide-react";
import { Seo, breadcrumbSchema } from "../components/Seo";
import { PageHero } from "../components/PageHero";
import { useSiteContent } from "../lib/useSiteContent";
import { JotformEmbed } from "../components/JotformEmbed";

const PATH = "/contact";

const Contact = () => {
  const sc = useSiteContent("contact", {
    eyebrow:    "Get In Touch",
    h1:         "Request Your",
    highlight:  "Free London Cleaning Quote",
    subheading: "Most quotes are sent within a couple of hours. Choose the channel that suits you — phone, WhatsApp, email or the form below.",
    quote_eyebrow: "Request a Quote",
    quote_heading: "Tell us about your property",
    quote_sub:     "Share your postcode, property size and approximate cleaning date. We'll send a fixed-price quote and propose a slot within a few working hours.",
  });
  return (
    <>
      <Seo
        title="Contact — End of Tenancy Cleaning Quote London"
        description="Contact Prime Tenancy Cleaning London for a free quote. Call 020 8036 3586, WhatsApp, email info@primetenancycleaning.com or request a quote online. 7 days a week."
        ogTitle="Contact Prime Tenancy Cleaning London"
        ogDescription="Get a free end of tenancy cleaning quote in hours. Phone, WhatsApp, email or online form — all London boroughs covered."
        path={PATH}
        schema={[
          breadcrumbSchema([{ name: "Contact", path: PATH }]),
          {
            "@context": "https://schema.org",
            "@type": "ContactPage",
            name: "Contact Prime Tenancy Cleaning",
            url: "https://primetenancycleaning.com/contact",
          },
        ]}
      />

      <PageHero
        eyebrow={sc.eyebrow}
        h1={sc.h1}
        highlight={sc.highlight}
        subheading={sc.subheading}
        breadcrumbs={[{ name: "Contact", path: PATH }]}
        image="https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=1920&q=80"
      />

      {/* Contact options grid */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              {
                icon: Phone,
                title: "Call us",
                value: "020 8036 3586",
                detail: "Mon–Sun 8am–8pm",
                href: "tel:+442080363586",
                testid: "contact-phone",
              },
              {
                icon: MessageCircle,
                title: "WhatsApp",
                value: "Quick chat",
                detail: "Average reply 5 min",
                href: "https://wa.me/447346006547",
                testid: "contact-whatsapp",
              },
              {
                icon: Mail,
                title: "Email us",
                value: "info@primetenancycleaning.com",
                detail: "Quotes within hours",
                href: "mailto:info@primetenancycleaning.com",
                testid: "contact-email",
              },
              {
                icon: Calendar,
                title: "Same-day slots",
                value: "Last-minute checkouts",
                detail: "Subject to availability",
                href: "#quote",
                testid: "contact-sameday",
              },
            ].map((c, i) => {
              const Icon = c.icon;
              return (
                <motion.a
                  key={c.title}
                  href={c.href}
                  target={c.href.startsWith("http") ? "_blank" : undefined}
                  rel={c.href.startsWith("http") ? "noopener noreferrer" : undefined}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  className="bg-slate-50 rounded-2xl p-7 hover:bg-navy hover:text-white transition-colors group block"
                  data-testid={c.testid}
                >
                  <div className="w-12 h-12 bg-teal/10 rounded-xl flex items-center justify-center mb-5 group-hover:bg-teal/20">
                    <Icon className="w-6 h-6 text-teal" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-navy group-hover:text-white mb-1.5 transition-colors">
                    {c.title}
                  </h3>
                  <p className="text-slate-700 font-medium group-hover:text-gold transition-colors mb-1">
                    {c.value}
                  </p>
                  <p className="text-slate-500 text-sm group-hover:text-slate-300 transition-colors">
                    {c.detail}
                  </p>
                </motion.a>
              );
            })}
          </div>
        </div>
      </section>

      {/* Quote form */}
      <section
        id="quote"
        className="py-20 md:py-28 bg-navy"
        data-testid="contact-quote-section"
      >
        <div className="container mx-auto px-6 md:px-12 lg:px-20">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-10"
          >
            <span className="text-teal font-semibold text-sm uppercase tracking-wider">{sc.quote_eyebrow}</span>
            <h2 className="font-heading text-3xl md:text-5xl font-semibold text-white mt-3 mb-4">{sc.quote_heading}</h2>
            <p className="text-slate-300 text-lg max-w-2xl mx-auto">{sc.quote_sub}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="max-w-3xl mx-auto"
          >
            <div className="bg-white rounded-2xl p-4 sm:p-8 md:p-10 overflow-hidden">
              <JotformEmbed testId="contact-jotform" />
              <p className="text-sm text-slate-500 mt-6 text-center">
                Prefer to talk? Call{" "}
                <a
                  href="tel:+442080363586"
                  className="text-teal font-semibold"
                >
                  020 8036 3586
                </a>{" "}
                or message us on{" "}
                <a
                  href="https://wa.me/447346006547"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-teal font-semibold"
                >
                  WhatsApp
                </a>
                .
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Hours & Coverage */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <div className="bg-slate-50 rounded-2xl p-7 border border-slate-100">
              <Clock className="w-7 h-7 text-teal mb-4" />
              <h3 className="font-heading text-lg font-semibold text-navy mb-3">
                Opening Hours
              </h3>
              <ul className="text-slate-700 text-sm space-y-1.5">
                <li>Monday – Friday: 8am – 8pm</li>
                <li>Saturday: 8am – 8pm</li>
                <li>Sunday: 9am – 6pm</li>
                <li>Bank holidays: available on request</li>
              </ul>
            </div>

            <div className="bg-slate-50 rounded-2xl p-7 border border-slate-100">
              <MapPin className="w-7 h-7 text-teal mb-4" />
              <h3 className="font-heading text-lg font-semibold text-navy mb-3">
                Service Area
              </h3>
              <p className="text-slate-700 text-sm leading-relaxed">
                All 32 London boroughs across Zone 1 to the M25. Same-day
                response in central London, planned next-day bookings for
                outer boroughs. See coverage on the{" "}
                <Link
                  to="/"
                  className="text-teal font-medium hover:text-navy"
                >
                  homepage map
                </Link>
                .
              </p>
            </div>

            <div className="bg-slate-50 rounded-2xl p-7 border border-slate-100">
              <Mail className="w-7 h-7 text-teal mb-4" />
              <h3 className="font-heading text-lg font-semibold text-navy mb-3">
                Agents & Landlords
              </h3>
              <p className="text-slate-700 text-sm leading-relaxed">
                Managing three or more properties? Email us at{" "}
                <a
                  href="mailto:info@primetenancycleaning.com"
                  className="text-teal font-medium hover:text-navy"
                >
                  info@primetenancycleaning.com
                </a>{" "}
                for portfolio billing and priority booking.
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
